import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Play, X } from "lucide-react";
import { toast } from "sonner";
import { listChecks } from "@/api/checks";
import { runFromSource } from "@/api/run-sources";
import type { CheckSummary, RunSource } from "@/api/types";

/**
 * Modal that opens before launching a saved-source run. Lets the user
 * pick which checks to snapshot onto the run, toggle force-rescan, and
 * see whether the resulting run will be "cheap" (word-index only) or
 * "expensive" (any check needs HTML / path fetches).
 *
 * Default selection: all enabled checks (matches the legacy
 * `init_run(check_ids=None)` behaviour).
 */
export function RunModal({
  source,
  onClose,
}: {
  source: RunSource;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const checks = useQuery({
    queryKey: ["checks"],
    queryFn: listChecks,
  });

  // Enabled checks are the only ones that match today's default
  // behaviour; disabled checks are an admin concern over on /checks.
  const enabledChecks = useMemo(
    () => (checks.data ?? []).filter((c) => c.enabled),
    [checks.data],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Virtual sources (the synthetic "All indexed domains" row + any
  // user-saved filter source) only make sense with force-rescan on —
  // otherwise `is_target_batch`'s "already-scanned" filter would skip
  // every URL. The backend forces this server-side too, so this UI
  // state is purely a display affordance.
  const virtual = source.kind === "virtual" || source.kind === "filter";
  const [forceRescan, setForceRescan] = useState(virtual);

  // Pre-check every enabled check the moment the list arrives.
  useEffect(() => {
    if (enabledChecks.length === 0) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(enabledChecks.map((c) => c.id)));
  }, [enabledChecks]);

  const selectedChecks = useMemo(
    () => enabledChecks.filter((c) => selected.has(c.id)),
    [enabledChecks, selected],
  );
  const anyExpensive = selectedChecks.some((c) => c.needs_html);

  const submit = useMutation({
    mutationFn: () =>
      runFromSource(source.id, {
        forceRescan,
        checkIds: Array.from(selected),
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["run-sources"] });
      qc.invalidateQueries({ queryKey: ["runs"] });
      if (!r.run_id) {
        toast(
          "All URLs in this source are already known, ignored, or already in Close.",
        );
        onClose();
        return;
      }
      toast.success("Run submitted");
      onClose();
      navigate({ to: "/runs/$runId", params: { runId: r.run_id } });
    },
  });

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = (on: boolean) =>
    setSelected(on ? new Set(enabledChecks.map((c) => c.id)) : new Set());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-2xl max-h-[85vh] flex flex-col">
        <header className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold">Run from source</h3>
            <p className="text-xs text-muted font-mono mt-0.5">{source.name}</p>
          </div>
          <button
            className="btn-ghost text-xs"
            onClick={onClose}
            disabled={submit.isPending}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {checks.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading checks…
            </div>
          ) : enabledChecks.length === 0 ? (
            <div className="text-sm text-muted">
              No enabled checks. Enable at least one on{" "}
              <a className="text-accent hover:underline" href="/checks">/checks</a>
              {" "}before running.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-muted">
                <span>
                  <strong className="text-fg">{selected.size}</strong> of{" "}
                  {enabledChecks.length} selected
                </span>
                <div className="flex gap-2">
                  <button
                    className="hover:text-fg"
                    onClick={() => toggleAll(true)}
                  >
                    All
                  </button>
                  <span>·</span>
                  <button
                    className="hover:text-fg"
                    onClick={() => toggleAll(false)}
                  >
                    None
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-border border border-border rounded-md">
                {enabledChecks.map((c) => (
                  <CheckRow
                    key={c.id}
                    check={c}
                    checked={selected.has(c.id)}
                    onToggle={() => toggle(c.id)}
                  />
                ))}
              </ul>
            </>
          )}

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={forceRescan}
              disabled={virtual}
              onChange={(e) => setForceRescan(e.target.checked)}
            />
            <span>
              <span className="font-medium">Force-rescan skipped domains</span>
              <span className="block text-xs text-muted">
                {virtual
                  ? "Required for virtual sources — every URL is by definition already known."
                  : "Re-evaluate previously-skipped domains against the selected checks. Already-extracted leads stay protected either way."}
              </span>
            </span>
          </label>
        </div>

        <footer className="px-5 py-4 border-t border-border flex items-center justify-between gap-3">
          <span className="text-sm">
            This run will be{" "}
            <CostLabel expensive={anyExpensive} />
          </span>
          <div className="flex gap-2">
            <button
              className="btn-ghost"
              onClick={onClose}
              disabled={submit.isPending}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={() => submit.mutate()}
              disabled={
                submit.isPending
                || selected.size === 0
                || enabledChecks.length === 0
              }
            >
              <Play className="h-4 w-4" />{" "}
              {submit.isPending ? "Submitting…" : "Run"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function CheckRow({
  check,
  checked,
  onToggle,
}: {
  check: CheckSummary;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-subtle/40">
        <input type="checkbox" checked={checked} onChange={onToggle} />
        <span className="flex-1 min-w-0 flex items-center gap-2">
          <span className="font-medium truncate">{check.name}</span>
          <span className="text-xs text-muted tabular-nums shrink-0">
            p{check.priority}
          </span>
          <span className="badge text-[10px] shrink-0">
            {check.action.name} ({check.action.spec.kind})
          </span>
        </span>
        <CostBadge needsHtml={check.needs_html} />
      </label>
    </li>
  );
}

function CostBadge({ needsHtml }: { needsHtml: boolean }) {
  // `needs_html=true` includes both HTML-only and mixed (html + word_index)
  // checks — even one HTML rule makes the whole check expensive at runtime.
  if (needsHtml) {
    return (
      <span className="badge text-[10px] shrink-0 bg-amber-500/15 text-amber-300 border border-amber-500/30">
        expensive
      </span>
    );
  }
  return (
    <span className="badge text-[10px] shrink-0 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
      cheap
    </span>
  );
}

function CostLabel({ expensive }: { expensive: boolean }) {
  if (expensive) {
    return (
      <strong className="text-amber-300">expensive · domains will be fetched</strong>
    );
  }
  return <strong className="text-emerald-300">cheap · offline word-index only</strong>;
}
